import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  // Остаток автоблокировки входа, секунды (0 — нет блокировки)
  const lockoutLeft = ref(0)
  const mustSetPassword = ref<boolean>(
    localStorage.getItem('must_set_password') === '1'
  )

  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isManager = computed(() => 
    user.value?.role === 'admin' || user.value?.role === 'manager'
  )

  // Initialize user from localStorage
  const initUser = () => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
      } catch {
        localStorage.removeItem('user')
      }
    }
  }

  const login = async (userId: number, password?: string) => {
    isLoading.value = true
    error.value = null
    lockoutLeft.value = 0
    
    try {
      const data = await api.login(userId, password)
      if (data.success) {
        token.value = data.token
        user.value = data.user
        mustSetPassword.value = !!data.must_set_password
        localStorage.setItem(
          'must_set_password',
          data.must_set_password ? '1' : '0'
        )
        // Store email for API identification
        if (data.user?.email) {
          localStorage.setItem('userEmail', data.user.email)
        }
        return data
      }
      error.value = data.error || 'Ошибка входа'
      return data
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка входа'
      const retryAfter = (e as { retryAfter?: unknown })?.retryAfter
      lockoutLeft.value = typeof retryAfter === 'number' && retryAfter > 0
        ? Math.floor(retryAfter)
        : 0
      return { success: false, error: error.value, retryAfter: lockoutLeft.value || undefined }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      await api.logout()
    } finally {
      token.value = null
      user.value = null
      mustSetPassword.value = false
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('userEmail')
      localStorage.removeItem('must_set_password')
    }
  }

  const loginByEmail = async (email: string, password?: string) => {
    isLoading.value = true
    error.value = null
    lockoutLeft.value = 0
    
    try {
      const data = await api.loginByEmail(email, password)
      if (data.success) {
        token.value = data.token
        user.value = data.user
        mustSetPassword.value = !!data.must_set_password
        localStorage.setItem(
          'must_set_password',
          data.must_set_password ? '1' : '0'
        )
        if (data.user?.email) {
          localStorage.setItem('userEmail', data.user.email)
        }
        return data
      }
      error.value = data.error || 'Ошибка входа'
      return data
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка входа'
      const retryAfter = (e as { retryAfter?: unknown })?.retryAfter
      lockoutLeft.value = typeof retryAfter === 'number' && retryAfter > 0
        ? Math.floor(retryAfter)
        : 0
      return { success: false, error: error.value, retryAfter: lockoutLeft.value || undefined }
    } finally {
      isLoading.value = false
    }
  }

  const setPassword = async (newPassword: string) => {
    isLoading.value = true
    error.value = null
    try {
      const updated = await api.updateProfile({ new_password: newPassword })
      const newUser = updated || user.value
      if (newUser) user.value = newUser
      mustSetPassword.value = false
      localStorage.removeItem('must_set_password')
      return { success: true, user: newUser }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Не удалось установить пароль'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const checkAuth = async () => {
    if (!token.value) return false
    
    try {
      const userData = await api.getCurrentUser()
      user.value = userData
      return true
    } catch {
      token.value = null
      user.value = null
      mustSetPassword.value = false
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('must_set_password')
      return false
    }
  }

  // Initialize on store creation
  initUser()

  return {
    token,
    user,
    isLoading,
    error,
    lockoutLeft,
    isAuthenticated,
    isAdmin,
    isManager,
    mustSetPassword,
    login,
    loginByEmail,
    setPassword,
    logout,
    checkAuth,
  }
})
